import type { ReactNode } from "react";
import { Alert, Button, Card, Center, Code, Group, List, Stack, Text, Title } from "@mantine/core";
import { IconLogout, IconRefresh, IconShieldX } from "@tabler/icons-react";
import { LoadingState } from "@/components/states";
import { isAdminToken } from "./claims";
import { useAuth } from "./useAuth";
import { SignInScreen } from "./SignInScreen";

/**
 * UX gate: renders nothing privileged until the operator's ID token satisfies
 * the SAME contract the backend enforces — `roles ⊇ ['admin']` AND a non-empty
 * `backendId`. This is purely a convenience layer; Firestore rules + the admin
 * API are the real boundary.
 */
export function AdminGate({ children }: { children: ReactNode }) {
  const { user, claims, loading, signOut, refreshClaims } = useAuth();

  if (loading) {
    return (
      <Center mih="100vh">
        <LoadingState label="Checking access…" />
      </Center>
    );
  }

  if (!user) return <SignInScreen />;

  if (!claims || !isAdminToken(claims)) {
    return (
      <Center mih="100vh" p="md">
        <Card withBorder shadow="sm" radius="md" w={520} p="xl">
          <Stack>
            <Group>
              <IconShieldX size={28} color="var(--mantine-color-red-6)" />
              <Title order={3}>No admin access</Title>
            </Group>
            <Text size="sm">
              Signed in as <b>{user.email ?? user.uid}</b>, but this token isn't a provisioned
              operator. Admin access requires both:
            </Text>
            <List size="sm" spacing={4}>
              <List.Item>
                <Code>roles</Code> contains <Code>admin</Code> —{" "}
                {claims?.isAdmin ? "✓ present" : "✗ missing"}
              </List.Item>
              <List.Item>
                a non-empty <Code>backendId</Code> claim —{" "}
                {claims?.backendId ? "✓ present" : "✗ missing"}
              </List.Item>
            </List>
            <Alert color="blue" variant="light">
              An existing admin must grant your backend user the <Code>admin</Code> role (via the
              Users screen). After that, refresh your claims or sign out and back in.
            </Alert>
            <Group justify="flex-end">
              <Button
                variant="default"
                leftSection={<IconRefresh size={16} />}
                onClick={() => void refreshClaims()}
              >
                Refresh claims
              </Button>
              <Button
                color="red"
                variant="light"
                leftSection={<IconLogout size={16} />}
                onClick={() => void signOut()}
              >
                Sign out
              </Button>
            </Group>
          </Stack>
        </Card>
      </Center>
    );
  }

  return <>{children}</>;
}
